import { useEffect, useRef, useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import type { Venue } from '../types';
import { getVenueCategories } from '../lib/venue-categories';
import NeighborhoodSelect from './NeighborhoodSelect';
import VenueCategoryMultiSelect from './VenueCategoryMultiSelect';

interface Props {
  venue: Venue | null;
  onClose: () => void;
  onSaved: (v: Venue) => void;
  /** Known neighborhood names to populate the dropdown. "+ Add new" is always available. */
  neighborhoodOptions?: string[];
}

type EditableFields =
  | 'name'
  | 'address'
  | 'partitionKey' // neighborhood
  | 'categories'
  | 'website'
  | 'instagramHandle'
  | 'facebookUrl'
  | 'twitterHandle'
  | 'lat'
  | 'lng'
  | 'active'
  | 'reviewStatus';

/**
 * UI-level moderation status. Maps to the underlying `(active, reviewStatus)`
 * pair when saving:
 *  - `active`    → `active=true,  reviewStatus='approved'`  (visible publicly)
 *  - `dismissed` → `active=false, reviewStatus='dismissed'` (venue hidden, events still shown)
 *  - `declined`  → `active=false, reviewStatus='declined'`  (venue + events hidden)
 */
type VenueStatus = 'active' | 'dismissed' | 'declined';

function venueToStatus(v: Venue): VenueStatus {
  if (v.active) return 'active';
  if (v.reviewStatus === 'declined') return 'declined';
  return 'dismissed';
}

interface FormState {
  name: string;
  address: string;
  partitionKey: string;
  categories: string[];
  website: string;
  instagramHandle: string;
  facebookUrl: string;
  twitterHandle: string;
  lat: string;
  lng: string;
  status: VenueStatus;
}

function toForm(v: Venue): FormState {
  return {
    name: v.name ?? '',
    address: v.address ?? '',
    partitionKey: v.partitionKey ?? '',
    categories: getVenueCategories(v),
    website: v.website ?? '',
    instagramHandle: v.instagramHandle ?? '',
    facebookUrl: v.facebookUrl ?? '',
    twitterHandle: v.twitterHandle ?? '',
    lat: typeof v.lat === 'number' ? String(v.lat) : '',
    lng: typeof v.lng === 'number' ? String(v.lng) : '',
    status: venueToStatus(v),
  };
}

export default function VenueEditModal({ venue, onClose, onSaved, neighborhoodOptions = [] }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [form, setForm] = useState<FormState | null>(venue ? toForm(venue) : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(venue ? toForm(venue) : null);
    setError(null);
  }, [venue]);

  useEffect(() => {
    if (!venue) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [venue, onClose]);

  if (!venue || !form) return null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Normalize: send empty strings as `undefined` so they clear the field
      // in Table Storage rather than storing literal empty strings.
      const parseCoord = (s: string): number | undefined => {
        const t = s.trim();
        if (!t) return undefined;
        const n = Number(t);
        return Number.isFinite(n) ? n : undefined;
      };
      // Map UI status → underlying (active, reviewStatus) pair.
      const statusMap: Record<VenueStatus, { active: boolean; reviewStatus: 'approved' | 'dismissed' | 'declined' }> = {
        active:    { active: true,  reviewStatus: 'approved' },
        dismissed: { active: false, reviewStatus: 'dismissed' },
        declined:  { active: false, reviewStatus: 'declined' },
      };
      const { active, reviewStatus } = statusMap[form.status];
      const patch: Partial<Record<EditableFields, string | number | boolean | string[] | undefined>> = {
        name: form.name.trim(),
        address: form.address.trim() || undefined,
        partitionKey: form.partitionKey.trim() || venue.partitionKey,
        categories: form.categories,
        website: form.website.trim() || undefined,
        instagramHandle: form.instagramHandle.trim() || undefined,
        facebookUrl: form.facebookUrl.trim() || undefined,
        twitterHandle: form.twitterHandle.trim() || undefined,
        lat: parseCoord(form.lat),
        lng: parseCoord(form.lng),
        active,
        reviewStatus,
      };
      const updated = await api.patch<Venue>(`/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`, patch);
      onSaved(updated);
      onClose();
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="venue-edit-title"
      onClick={onClose}
    >
      <div className="mh-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="venue-edit-title" className="h5 mb-0 flex-grow-1">Edit venue</h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </header>
        <form
          className="p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <div className="row g-3">
            <div className="col-md-8">
              <label htmlFor="venue-name" className="form-label small fw-semibold">Name *</label>
              <input
                id="venue-name"
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="venue-status" className="form-label small fw-semibold">Status</label>
              <select
                id="venue-status"
                className="form-select"
                value={form.status}
                onChange={(e) => setField('status', e.target.value as VenueStatus)}
                aria-describedby="venue-status-help"
              >
                <option value="active">Active (visible publicly)</option>
                <option value="dismissed">Inactive — hidden, keep events</option>
                <option value="declined">Declined — hide venue and its events</option>
              </select>
              <div id="venue-status-help" className="form-text small">
                Dismissed venues are hidden from venue lists but their events still appear. Declined also hides the events.
              </div>
            </div>

            <div className="col-md-12">
              <label htmlFor="venue-address" className="form-label small fw-semibold">Address</label>
              <input
                id="venue-address"
                type="text"
                className="form-control"
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="venue-hood" className="form-label small fw-semibold">Neighborhood</label>
              <NeighborhoodSelect
                id="venue-hood"
                className="form-select"
                value={form.partitionKey}
                options={neighborhoodOptions}
                onChange={(next) => setField('partitionKey', next)}
              />
              <div className="form-text small">Pick from existing or add a new one. Changing this pins the venue (manual override).</div>
            </div>
            <div className="col-md-6">
              <label htmlFor="venue-category" className="form-label small fw-semibold">Categories</label>
              <VenueCategoryMultiSelect
                id="venue-category"
                value={form.categories}
                onChange={(next) => setField('categories', next)}
                ariaLabel={`Categories for ${venue.name}`}
              />
              <div className="form-text small">Pick one or more. Drives how this venue is grouped and filtered.</div>
            </div>

            <div className="col-md-12">
              <label htmlFor="venue-website" className="form-label small fw-semibold">Website</label>
              <input
                id="venue-website"
                type="url"
                className="form-control"
                value={form.website}
                onChange={(e) => setField('website', e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            <div className="col-md-4">
              <label htmlFor="venue-ig" className="form-label small fw-semibold">Instagram handle</label>
              <div className="input-group">
                <span className="input-group-text">@</span>
                <input
                  id="venue-ig"
                  type="text"
                  className="form-control"
                  value={form.instagramHandle}
                  onChange={(e) => setField('instagramHandle', e.target.value.replace(/^@/, ''))}
                />
              </div>
            </div>
            <div className="col-md-4">
              <label htmlFor="venue-fb" className="form-label small fw-semibold">Facebook URL</label>
              <input
                id="venue-fb"
                type="url"
                className="form-control"
                value={form.facebookUrl}
                onChange={(e) => setField('facebookUrl', e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="venue-tw" className="form-label small fw-semibold">Twitter / X handle</label>
              <div className="input-group">
                <span className="input-group-text">@</span>
                <input
                  id="venue-tw"
                  type="text"
                  className="form-control"
                  value={form.twitterHandle}
                  onChange={(e) => setField('twitterHandle', e.target.value.replace(/^@/, ''))}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label htmlFor="venue-lat" className="form-label small fw-semibold">Latitude</label>
              <input
                id="venue-lat"
                type="text"
                inputMode="decimal"
                className="form-control"
                value={form.lat}
                onChange={(e) => setField('lat', e.target.value)}
                placeholder="e.g. 30.3273"
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="venue-lng" className="form-label small fw-semibold">Longitude</label>
              <input
                id="venue-lng"
                type="text"
                inputMode="decimal"
                className="form-control"
                value={form.lng}
                onChange={(e) => setField('lng', e.target.value)}
                placeholder="e.g. -81.6557"
              />
              <div className="form-text small">Used for the venue map and “near me” distance sort.</div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
